// @flow

import styled from '@emotion/styled'
import { useLocation } from '@reach/router'
import { Link, navigate, withPrefix } from 'gatsby'
import { StaticImage } from 'gatsby-plugin-image'
import * as React from 'react'

import * as appLinks from '@toggl/app-links'
import { colors, fonts, media } from '@toggl/style'
import {
  Button,
  Card,
  ClientRender,
  Hero,
  Responsive,
  Reviews,
  Search,
  SectionBlock,
  SectionMobileNoBleedBlock,
  text,
} from '@toggl/ui'
import { IconArrowBigLeft } from '@toggl/ui/src/icons/IconArrow'
import { IconCheck } from '@toggl/ui/src/icons/IconCheck'
import { IconClose } from '@toggl/ui/src/icons/IconClose'
import { IconInfo } from '@toggl/ui/src/icons/IconInfo'
import { useAssets } from 'components'
import { LayoutTrack } from 'components/layouts'
import Signup from 'components/Signup'

import yamlData from './integrations.yml'
import type { Category, IntegrationType } from './integrations.yml'

type FiltersState = {|
  +category: Category | null,
  +type: IntegrationType | null,
  +term: string | null,
|}

const typeNames = Object.fromEntries(
  yamlData.sections.integrations.types.map((type) => [type.id, type.name])
)

const categoryNames = Object.fromEntries(
  yamlData.sections.integrations.categories.map((category) => [
    category.id,
    category.name,
  ])
)

function useFilters(): FiltersState {
  const { search } = useLocation()
  return React.useMemo(() => {
    if (!search) return defaultFilters
    const params = new URLSearchParams(search)

    const category =
      yamlData.sections.integrations.categories.find(
        (category) => category.id === params.get('category')
      )?.id || null
    const type =
      yamlData.sections.integrations.types.find(
        (type) => type.id === params.get('type')
      )?.id || null
    const term = params.get('term')

    return {
      category,
      type,
      term,
    }
  }, [search])
}

export default function IntegrationsPage() {
  const {
    sections: { integrations },
    title,
    seoMetaDescription,
    ogImage,
  } = yamlData
  const uploads = useAssets({
    ogImage,
  })

  return (
    <LayoutTrack
      title={title}
      description={seoMetaDescription}
      ogImage={uploads.ogImage}
      showRatingSchema
    >
      <PageHero />
      <ClientRender hideServerRender>
        <ProductListAndFilter integrations={integrations} />
      </ClientRender>

      <SectionMobileNoBleedBlock>
        <Responsive.DesktopOnly>
          <SignupTitle>
            Start <Rotalic>integrating</Rotalic> Toggl Track into your workflow
          </SignupTitle>
        </Responsive.DesktopOnly>
        <Signup demoSource="Integrations" />
      </SectionMobileNoBleedBlock>

      <Reviews
        image={
          <StaticImage
            src="../../../assets/reviews-badge-desktop.png"
            alt="Toggl Track reviews: 4.5 out of 5 stars in Capterra, GetApp, Software Advice and GCrowd"
            width={1200}
            formats={['auto', 'webp', 'avif']}
            placeholder="none"
            objectFit="contain"
          />
        }
        mobileImage={
          <StaticImage
            src="../../../assets/reviews-badge-mobile.png"
            alt="Toggl Track reviews: 4.5 out of 5 stars in Capterra, GetApp, Software Advice and GCrowd"
            width={300}
            formats={['auto', 'webp', 'avif']}
            placeholder="none"
            objectFit="contain"
          />
        }
      />
    </LayoutTrack>
  )
}

type FilterPillListProps = {|
  +filters: FiltersState,
  +applyFilter: (filters: $Shape<FiltersState>) => void,
|}

const FilterPillList = ({ applyFilter, filters }: FilterPillListProps) => {
  const term = filters.term ? `“${filters.term}”` : ''
  const type = typeNames[filters.type]
  const category = categoryNames[filters.category]

  return (
    <React.Fragment>
      {filters.term && (
        <FilterPill filter="term" applyFilter={applyFilter}>
          {term}
        </FilterPill>
      )}
      {filters.type && (
        <FilterPill filter="type" applyFilter={applyFilter}>
          {type}
        </FilterPill>
      )}
      {filters.category && (
        <FilterPill filter="category" applyFilter={applyFilter}>
          {category}
        </FilterPill>
      )}
    </React.Fragment>
  )
}

type FilterPillProps = {|
  +children?: React.Node,
  +filter: $Keys<FiltersState>,
  +applyFilter: (filters: $Shape<FiltersState>) => void,
|}

const FilterPill = ({ applyFilter, children, filter }: FilterPillProps) => {
  const clearFilter = () => applyFilter({ [(filter: string)]: null })
  return (
    <FilterPillWrapper>
      {children}
      <ClearIconWrapper onClick={clearFilter}>
        <IconClose />
      </ClearIconWrapper>
    </FilterPillWrapper>
  )
}

const defaultFilters: FiltersState = { category: null, type: null, term: null }

const areEmptyFilters = (filters: FiltersState) =>
  !filters.type && !filters.category && !filters.term

const ProductLink = ({ children, href, to }) =>
  href ? (
    <a
      {...(href.startsWith('/')
        ? { href: withPrefix(href) }
        : {
            href: appLinks[href] ?? href,
            rel: 'noopener noreferrer',
            target: '_blank',
          })}
    >
      {children}
    </a>
  ) : to ? (
    <Link to={to}>{children}</Link>
  ) : null

const Rotalic = styled.span`
  font-family: ${fonts.gtHaptikMediumRotalic};
  color: ${colors.pink};
`

const HeroContentWrapper = styled(Hero.ContentWrapper)`
  padding-bottom: 0 !important;
`

const HeroContent = styled.div`
  ${media.mq[1]} {
    display: flex;
    gap: 30px;
    align-items: center;
  }
  ${media.mq[2]} {
    gap: 60px;
  }
  p {
    margin: 20px 0;
  }
`

const HeroLeft = styled.div`
  flex: 1 1 40%;
  ${media.mq[1]} {
    padding-bottom: 30px;
  }
`

const HeroRight = styled.div`
  text-align: center;
  .gatsby-image-wrapper {
    max-width: 420px;
    margin: auto;
  }
`

const ExtensionWrapper = styled.div`
  background: ${colors.yellow};
  ${media.mq[1]} {
    display: flex;
    align-items: center;
    grid-row: 4;
    grid-column: 1 / -1;
  }
`

const ExtensionText = styled.div`
  h4,
  p {
    margin-bottom: 15px;
  }
  padding: 25px;
  ${media.mq[2]} {
    padding: 30px 0 30px 40px;
  }
  p > a {
    color: ${colors.darkPurple};
    text-decoration: underline;
    &:hover {
      color: ${colors.pink};
    }
  }
`

const ButtonImage = styled.button`
  font-family: ${fonts.gtHaptikMedium};
  border: none;
  border-radius: 200px;
  cursor: pointer;
  display: flex;
  align-items: center;
  column-gap: 8px;
  background: ${colors.white};
  color: ${colors.darkPurple};
  padding: 10px 12px;
  text-decoration: none;
  &:hover {
    background: ${colors.fadedPurpleAlt};
    color: ${colors.white};
  }
`

const ExtensionLink = styled.a`
  display: inline-block;
  margin: 10px 10px 0 0;
`

const ExtensionImage = styled.div`
  display: none;
  ${media.mq[2]} {
    display: block;
    transform: translateY(10px);
    margin-right: 20px;
    flex: 0 0 170px;
  }
`

const FilterSectionWrapper = styled.div`
  margin: 0 20px;
  ${media.mq[1]} {
    width: 35%;
    max-width: 280px;
  }
`

const FilterSection = styled.div`
  margin: 3rem 0;
  color: ${colors.darkPurple};
  & ${text.P4} {
    font-weight: 700;
    text-transform: uppercase;
    color: ${colors.purple60};
    margin-bottom: 0.5rem;
  }
`
const DownloadSection = styled(FilterSection)`
  display: none;
  ${media.mq[1]} {
    display: block;
    button:hover {
      background: ${colors.darkPurple20};
    }
  }
`

const FilterList = styled.ul`
  list-style: none;
  margin: 1rem 0 0;
  padding: 0;
`

const FilterListItem = styled<{
  ...React.ElementConfig<'li'>,
  active: boolean,
}>('li')`
  cursor: pointer;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  position: relative;
  > svg:nth-of-type(1) {
    display: none;
    position: absolute;
    left: -25px;
  }
  ${({ active }) =>
    active &&
    `
      color: ${colors.pink};
      > svg:nth-of-type(1) {
        display: block;
      }
    `}
  &:hover {
    color: ${colors.pink80};
  }

  & svg {
    fill: ${colors.fadedPurpleAlt};
  }
`

const TooltipContainer = styled.span`
  position: relative;
  cursor: pointer;
  margin-left: auto;
  &:before,
  &:after {
    opacity: 0;
    visibility: hidden;
    transition: 0.15s ease;
    left: 50%;
    position: absolute;
    z-index: 10;
  }
  &:before {
    content: '';
    height: 0;
    width: 0;
    border-style: solid;
    border-color: ${colors.pink70} transparent transparent transparent;
    border-width: 8px 8px 0 8px;
    bottom: calc(100% + 2px);
    transform: translateX(-50%);
  }
  &:after {
    content: attr(data-title);
    transform: translateX(-90%);
    background: ${colors.pink70};
    font-family: ${fonts.inter};
    ${text.paragraph4};
    color: ${colors.darkPurple};
    padding: 15px 20px;
    bottom: calc(100% + 8px);
    width: 250px;
  }
  &:hover {
    &:before,
    &:after {
      visibility: visible;
      opacity: 1;
    }
    svg {
      fill: ${colors.purple60};
    }
  }
  ${media.mq[2]} {
    &:after {
      transform: translateX(-50%);
    }
  }
`

const FilterInfo = styled.div`
  margin-bottom: 30px;
`

const FilterPillWrapper = styled.div`
  ${text.paragraph4};
  display: inline-flex;
  align-items: center;
  padding-left: 1rem;
  margin: 0.25rem;
  height: 2rem;
  border-radius: 1rem;
  background: ${colors.darkPurple20};
  color: ${colors.fadedPurple};
  &:hover {
    background: ${colors.darkPurple20};
  }
`

const ClearIconWrapper = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
  padding: 0 0.6rem;
  margin-left: 0.5rem;
  border-left: 1px solid ${colors.lightPurple};
  color: ${colors.fadedPurple};
  cursor: pointer;
  & svg {
    width: 0.625rem;
  }
`

const ResultSection = styled.div`
  flex: 1;
`

const NoResults = styled(text.P1)`
  padding: 40px;
  text-align: center;
  color: ${colors.darkPurple};
  max-width: 500px;
  margin: 40px auto;
  button {
    margin: 20px auto;
  }
`

const Products = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 15px;
  ${media.mq[2]} {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-column-gap: 40px;
    grid-row-gap: 50px;
  }
`

const ProductIcon = styled.img`
  max-width: 4rem;
  max-height: 2rem;
  ${media.mq[2]} {
    max-width: 9rem;
    max-height: 4rem;
  }
`

const CardImageTextSimple = styled(Card.ImageTextSimple)`
  &:not(:last-child) {
    margin-bottom: 25px;
  }
`

const ProductTypePillList = styled.div`
  margin: 0.75rem 0;
`

const ProductTypePill = styled.span`
  ${text.paragraph5};
  display: inline-block;
  padding: 0.35rem 0.6rem;
  margin: 0 0.3rem 0.3rem 0;
  border-radius: 0.25rem;
  background: ${colors.pink30};
  color: ${colors.fadedPurple};
`

const OtherIntegrationWrapper = styled.div`
  text-align: center;
  margin: 60px 0 30px;
  h4 {
    margin: 10px 0;
  }
  p {
    margin-top: 20px;
    > a {
      color: ${colors.darkPurple};
      text-decoration: underline;
    }
  }

  ${media.mq[1]} {
    margin-top: 100px;
  }
`

const OtherIntegration = styled.div`
  ${media.mq[1]} {
    display: flex;
    gap: 15px;
  }
`

const BoxLink = styled.a`
  ${text.paragraph2};
  font-family: ${fonts.gtHaptikMedium};
  line-height: 1.3;
  display: block;
  text-align: left;
  margin: 15px 0;
  background: ${colors.fadedPurpleAlt};
  padding: 20px 50px 20px 25px;
  position: relative;
  &:hover {
    background: ${colors.darkPurple};
    svg {
      transform: scaleX(-1) translate(-20%, -50%);
    }
  }
  svg {
    width: 25px;
    position: absolute;
    transform: scaleX(-1) translateY(-50%);
    transition: 0.4s ease-in-out;
    top: 50%;
    right: 15px;
  }
  ${media.mq[1]} {
    flex: 1;
  }
  ${media.mq[2]} {
    padding-right: 85px;
    svg {
      width: 40px;
      right: 20px;
    }
  }
`

const SignupTitle = styled(text.H2)`
  text-align: center;
  margin: 50px auto;
  max-width: 700px;
`

const PageHero = React.memo(function PageHero() {
  return (
    <Hero.Wrapper>
      <HeroContentWrapper>
        <HeroContent>
          <HeroLeft>
            <text.H1 size="medium" color={colors.almostWhite}>
              <Rotalic>Supercharge</Rotalic> your workflow with integrations,
              APIs, and automations
            </text.H1>
            <text.P2 color={colors.almostWhite}>
              Track time from inside 100+ tools via browser extensions, sync
              data to Toggl Track with our native integrations, and more.
            </text.P2>
          </HeroLeft>
          <HeroRight>
            <StaticImage
              src="./assets/integrations-balloons.png"
              alt="A cute bot surrounded by balloons with logos of apps Toggl Track integrations with: Jira, Slack, GitHub, Todoist"
              width={650}
              formats={['auto', 'webp', 'avif']}
              placeholder="none"
              objectFit="contain"
            />
          </HeroRight>
        </HeroContent>
      </HeroContentWrapper>
    </Hero.Wrapper>
  )
})

function ProductListAndFilter({ integrations }) {
  const filters = useFilters()
  const applyFilter = React.useCallback(
    (newFilters: $Shape<FiltersState>) => {
      const updatedFilters = Object.entries({
        ...filters,
        ...newFilters,
      }).filter(([, value]) => value)
      const search = new URLSearchParams((updatedFilters: any))
      navigate(`?${search.toString()}#_`)
    },
    [filters]
  )

  const onSearch = React.useCallback(
    (term: string) => {
      applyFilter({ term })
    },
    [applyFilter]
  )

  const sortedProducts = React.useMemo(() => {
    return integrations.products.slice().sort((a, b) => {
      if (a.featured === b.featured) {
        return a.name.localeCompare(b.name)
      }
      return a.featured ? -1 : 1
    })
  }, [integrations])

  const visibleProducts = React.useMemo(() => {
    if (areEmptyFilters(filters)) return sortedProducts

    const filterTerm = filters.term?.toLowerCase() || ''

    return sortedProducts.filter(
      ({ cat, type, name }) =>
        name.toLowerCase().includes(filterTerm) &&
        (!filters.category || cat.includes(filters.category)) &&
        (!filters.type || type.includes(filters.type))
    )
  }, [filters, sortedProducts])

  return (
    <SectionBlock background={colors.almostWhite} flex gap="30px">
      <FilterSectionWrapper>
        <Search
          onSearch={onSearch}
          placeholder={integrations.searchPlaceholder}
          initialValue={filters.term}
        />

        <FilterSection>
          <text.P4>Type</text.P4>
          <FilterList>
            {integrations.types.map((type) => (
              <FilterListItem
                key={type.id}
                active={filters.type === type.id}
                onClick={() =>
                  applyFilter({
                    type: filters.type !== type.id ? type.id : null,
                  })
                }
              >
                <IconCheck />
                {type.name}
                <TooltipContainer data-title={type.tooltip}>
                  <IconInfo />
                </TooltipContainer>
              </FilterListItem>
            ))}
          </FilterList>
        </FilterSection>

        <FilterSection>
          <text.P4>Categories</text.P4>
          <FilterList>
            {integrations.categories.map((category) => (
              <FilterListItem
                key={category.id}
                active={filters.category === category.id}
                onClick={() =>
                  applyFilter({
                    category:
                      filters.category !== category.id ? category.id : null,
                  })
                }
              >
                <IconCheck />
                {category.name}
              </FilterListItem>
            ))}
          </FilterList>
        </FilterSection>

        <DownloadSection>
          <text.P4>Get the browser extensions</text.P4>
          <ExtensionLink
            href={appLinks.EXTENSION_CHROME}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button.WithImage
              tabIndex="-1"
              image={
                <StaticImage
                  src="./assets/chrome.png"
                  alt="Toggl Track plugin for Google Chrome"
                  layout="fixed"
                  width={48}
                  height={48}
                  formats={['auto', 'webp', 'avif']}
                  placeholder="none"
                />
              }
              title="Toggl Track extension"
              subtitle="for Google Chrome"
            />
          </ExtensionLink>
          <ExtensionLink
            href={appLinks.EXTENSION_FIREFOX}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button.WithImage
              tabIndex="-1"
              image={
                <StaticImage
                  src="./assets/firefox.png"
                  alt="Toggl Track plugin for Firefox"
                  layout="fixed"
                  width={48}
                  height={48}
                  formats={['auto', 'webp', 'avif']}
                  placeholder="none"
                />
              }
              title="Toggl Track extension"
              subtitle="for Firefox"
            />
          </ExtensionLink>
        </DownloadSection>
      </FilterSectionWrapper>
      <ResultSection>
        <FilterInfo>
          <strong>
            {!areEmptyFilters(filters) &&
              `${visibleProducts.length} ${
                visibleProducts.length !== 1 ? 'results' : 'result'
              }`}
          </strong>
          {!areEmptyFilters(filters) && ` filtered by `}
          <FilterPillList filters={filters} applyFilter={applyFilter} />
        </FilterInfo>
        {visibleProducts.length ? (
          <Products>
            {visibleProducts.map((integration) => (
              <ProductLink
                href={integration.href}
                to={integration.to}
                key={integration.id}
              >
                <CardImageTextSimple
                  image={
                    <ProductIcon
                      // $FlowFixMe[unsupported-syntax]
                      src={require(`./assets/${integration.icon}`)}
                      alt={`${integration.name} logo`}
                      loading="lazy"
                      width="130"
                      height="80"
                    />
                  }
                  title={integration.name}
                  description={integration.description}
                  label={integration.featured ? integrations.featuredLabel : ''}
                >
                  <ProductTypePillList>
                    {integration.type.map((type) => (
                      <ProductTypePill key={type}>
                        {typeNames[type]}
                      </ProductTypePill>
                    ))}
                  </ProductTypePillList>
                </CardImageTextSimple>
              </ProductLink>
            ))}

            <ExtensionWrapper>
              <ExtensionText>
                <text.H4 color={colors.darkPurple}>
                  Get the Toggl Track browser extensions
                </text.H4>
                <text.P3 color={colors.fadedPurple}>
                  Track time on the web with a plugin or in 100+ online apps via
                  our Chrome and Firefox browser extensions, which embed the
                  Toggl Track timer into the interface of enabled apps.{' '}
                  <a href="https://support.toggl.com/en/articles/2206984-toggl-track-browser-extension">
                    Learn more about how it works here.
                  </a>
                </text.P3>
                <ExtensionLink
                  href={appLinks.EXTENSION_CHROME}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ButtonImage tabIndex="-1">
                    <StaticImage
                      src="./assets/chrome.png"
                      alt="Toggl Track plugin for Google Chrome"
                      width={25}
                      height={25}
                      formats={['auto', 'webp', 'avif']}
                      placeholder="none"
                    />
                    Get for Chrome
                  </ButtonImage>
                </ExtensionLink>
                <ExtensionLink
                  href={appLinks.EXTENSION_FIREFOX}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ButtonImage tabIndex="-1">
                    <StaticImage
                      src="./assets/firefox.png"
                      alt="Toggl Track plugin for Firefox"
                      width={25}
                      height={25}
                      formats={['auto', 'webp', 'avif']}
                      placeholder="none"
                    />
                    Get for Firefox
                  </ButtonImage>
                </ExtensionLink>
              </ExtensionText>
              <ExtensionImage>
                <StaticImage
                  src="../../../assets/illo-toggl-button.png"
                  alt="3D illustration of a hand clicking on a button"
                  width={200}
                  formats={['auto', 'webp', 'avif']}
                  placeholder="none"
                  objectFit="contain"
                />
              </ExtensionImage>
            </ExtensionWrapper>
          </Products>
        ) : (
          <NoResults>
            Oops, there are no results. You should try broadening your search.
            <Button.Primary onClick={() => applyFilter(defaultFilters)}>
              Remove filters
            </Button.Primary>
          </NoResults>
        )}
        <OtherIntegrationWrapper>
          <text.H4 color={colors.darkPurple}>
            Can't find an integration?
          </text.H4>
          <OtherIntegration>
            <BoxLink
              href="https://github.com/toggl/toggl_api_docs"
              caretDirection="right"
            >
              Build something on your own with the Toggl Track API
              <IconArrowBigLeft color={colors.lightBeige} />
            </BoxLink>
            <BoxLink
              href="https://support.toggl.com/en/articles/2565060-user-developed-integrations"
              caretDirection="right"
            >
              Discover integrations built by our user community
              <IconArrowBigLeft color={colors.lightBeige} />
            </BoxLink>
          </OtherIntegration>
          <text.P3 color={colors.darkPurple}>
            Or <Link to="/track/contact/">get in touch</Link> to let us know!
            Your feedback makes us better.
          </text.P3>
        </OtherIntegrationWrapper>
      </ResultSection>
    </SectionBlock>
  )
}
